"use client";

import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { listTimeZones, matchesZoneQuery, zoneDisplayLabel, zoneOffsetLabel } from "@/lib/timezone";
import { Check } from "lucide-react";
import { useMemo, useRef, useState } from "react";

/**
 * Searchable IANA zone picker. One implementation for the whole app — the
 * automation trigger and the Preferences panel both had their own before.
 *
 * The list is long (~400 zones), so it is filtered rather than paginated and
 * the current value is pinned to the top, the way Notion's picker does it.
 */

interface Props {
  value: string | undefined;
  onSelect: (zone: string) => void;
  disabled?: boolean;
  /** Shown when there is no value yet. */
  placeholder?: string;
}

export function TimeZonePicker({ value, onSelect, disabled, placeholder = "Select a timezone" }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);

  const zones = useMemo(() => listTimeZones(), []);
  const filtered = useMemo(() => {
    const matches = zones.filter((zone) => matchesZoneQuery(zone, query));
    if (!value) return matches;
    // Current value first, so opening the picker shows what is selected.
    return [value, ...matches.filter((zone) => zone !== value)];
  }, [zones, query, value]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="user-select-none inline-flex h-8 min-w-[180px] items-center justify-between gap-2 whitespace-nowrap rounded-md border border-zinc-300 px-3 text-sm font-medium leading-[1.2] text-zinc-900 transition-colors duration-200 ease-in hover-component disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-100"
      >
        <span className="truncate">{value ? zoneDisplayLabel(value) : placeholder}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="block h-3.5 w-3.5 flex-shrink-0 fill-zinc-500 dark:fill-zinc-400"
        >
          <path d="m12.76 6.52-4.32 4.32a.62.62 0 0 1-.44.18.62.62 0 0 1-.44-.18L3.24 6.52a.63.63 0 0 1 0-.88c.24-.24.64-.24.88 0L8 9.52l3.88-3.88c.24-.24.64-.24.88 0s.24.64 0 .88" />
        </svg>
      </button>

      {open && (
        <AnchoredPopover
          anchor={triggerRef}
          onClose={close}
          placement="bottom-end"
          offsetPx={6}
          className="z-[10000] w-72 rounded-xl border border-component bg-modal p-1 text-popover-foreground shadow-lg outline-none"
        >
          <input
            // biome-ignore lint/a11y/noAutofocus: the picker exists to be typed into
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city or timezone…"
            aria-label="Search timezones"
            className="mb-1 w-full rounded-md bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-zinc-500"
          />
          <div className="clean-scrollbar max-h-72 overflow-y-auto">
            {filtered.length === 0 && <div className="px-2 py-3 text-sm text-zinc-500">No matching timezone</div>}
            {filtered.slice(0, 200).map((zone) => (
              <button
                key={zone}
                type="button"
                onClick={() => {
                  onSelect(zone);
                  close();
                }}
                className="hover-component flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {value === zone && <Check size={14} />}
                </span>
                <span className="min-w-0 flex-1 truncate">{zone.replace(/_/g, " ")}</span>
                <span className="flex-shrink-0 text-xs text-zinc-500 dark:text-zinc-400">{zoneOffsetLabel(zone)}</span>
              </button>
            ))}
          </div>
        </AnchoredPopover>
      )}
    </div>
  );
}
